/**
 * Typed wrappers for the `/salescoach/api/overview/*` + `/salescoach/api/sync-status`
 * endpoints (Plan 06 Task 6.2).
 *
 * Every call:
 *   1. Strips defaults from the filter object (short URL / cache-key friendly).
 *   2. Resolves the preset to a concrete `from`/`to` so the server never has
 *      to re-interpret the preset string.
 *   3. Passes the result through `qs.stringify` (arrayFormat='comma' — matches
 *      how `use-url-filters` serialises).
 *   4. Returns the raw `ApiEnvelope<T>`. Unwrapping is the hook's job (Task 6.3).
 *
 * URL construction is centralised through `buildApiUrl` so endpoint wrappers
 * cannot accidentally emit a query-string key twice (bug-2: Express 5
 * coerces duplicate keys to an array, tripping Zod enum validation on the
 * trend endpoints). When `extras.granularity` is set, `granularity` is
 * stripped from the filter projection so it appears exactly once in the
 * final URL.
 */

import qs from 'qs';

import { apiFetch } from '@/services/api';
import {
  filtersQuestion,
  resolveRange,
  stripDefaults,
  type Filters,
} from '@/lib/filters-schema';
import type {
  ActivityTrendPoint,
  ApiEnvelope,
  FeedbackRow,
  OverviewKpis,
  RatingBucket,
  SyncStatus,
  TimeSavedBreakdownItemsResponse,
  TimeSavedBreakdownResponse,
  TimeSavedSeries,
  TraceListExtras,
  TraceListResponse,
} from './api-types';

/**
 * Scalar-valued extra query parameters that endpoint wrappers can attach on
 * top of the filter projection. `number | boolean` are stringified through
 * `qs.stringify` and therefore don't need explicit coercion. `undefined`
 * entries are dropped — pass them straight through for optional params.
 */
export type ApiUrlExtras = Partial<{
  granularity: Filters['granularity'];
  limit: number;
  offset: number;
  sort: string;
  source: string;
  minRatings: number;
  includePrev: boolean;
}> &
  Record<string, string | number | boolean | undefined>;

/**
 * Build the query string suffix for a API call. Always emits `from`/`to`
 * (resolved from preset) so the server can skip preset interpretation; the
 * preset itself is kept so period-compare math matches exactly (the "all"
 * preset has a special prev-period rule — see IA §1.4).
 *
 * NOTE: kept for backward compatibility with callers that already assembled
 * their own extra params. New callers should prefer `buildApiUrl`.
 */
export function buildQuery(filters: Filters): string {
  const range = resolveRange(filters);
  const stripped = stripDefaults(filters);
  // Serialise `users` as comma so the server sees the same format the URL
  // carries; `qs.stringify` handles missing / empty arrays correctly.
  const query = qs.stringify(
    { ...stripped, from: range.from, to: range.to },
    { arrayFormat: 'comma' },
  );
  return query ? `?${query}` : '';
}

/**
 * Canonical builder for every `/salescoach/api/*` endpoint URL. One function owns
 * query-string construction end-to-end — removing the class of bugs where
 * the filter serialiser and a per-endpoint wrapper each emit the same key
 * (`granularity` was emitted twice before this was centralised; Express 5
 * then coerced it to an array and Zod rejected the request).
 *
 * Rules:
 *   - Defaults stripped from `filters` via `stripDefaults`.
 *   - `from` / `to` always resolved from preset and emitted.
 *   - Any `extras` value that overlaps with a filter key (`granularity`) wins
 *     and the filter copy is stripped so the key appears exactly once.
 *   - `undefined` extras are dropped (don't emit `granularity=undefined`).
 *   - Arrays serialise via `arrayFormat: 'comma'` (parity with `buildQuery`).
 */
export function buildApiUrl(
  path: string,
  filters: Filters,
  extras: ApiUrlExtras = {},
): string {
  const range = resolveRange(filters);
  /* `filtersQuestion`, not `stripDefaults`: the per-table sort keys are client
     state (they make a sorted table bookmarkable) and no endpoint reads them,
     so sending them made every request URL change on a column-header click.
     Endpoints that DO sort get it explicitly, through `extras.sort`. */
  const stripped = { ...filtersQuestion(filters) } as Record<string, unknown>;

  // Drop any filter-projection key that `extras` also provides, so
  // `qs.stringify` never sees the same name twice. Keys whose extras value
  // is `undefined` fall back to the filter projection (nothing is dropped).
  const cleanedExtras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(extras)) {
    if (value === undefined) continue;
    cleanedExtras[key] = value;
    // Strip the overlapping filter-projection key so we emit exactly once.
    delete stripped[key];
  }

  const query = qs.stringify(
    {
      ...stripped,
      from: range.from,
      to: range.to,
      ...cleanedExtras,
    },
    { arrayFormat: 'comma' },
  );
  const suffix = query ? `?${query}` : '';
  return `${path}${suffix}`;
}

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

export async function getOverviewKpis(
  filters: Filters,
): Promise<ApiEnvelope<OverviewKpis>> {
  return apiFetch<ApiEnvelope<OverviewKpis>>(
    buildApiUrl('/salescoach/api/overview/kpis', filters),
  );
}

export async function getActivityTrend(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<ActivityTrendPoint[]>> {
  return apiFetch<ApiEnvelope<ActivityTrendPoint[]>>(
    buildApiUrl('/salescoach/api/overview/activity-trend', filters, { granularity }),
  );
}

export async function getRatingDistribution(
  filters: Filters,
): Promise<ApiEnvelope<RatingBucket[]>> {
  return apiFetch<ApiEnvelope<RatingBucket[]>>(
    buildApiUrl('/salescoach/api/overview/rating-distribution', filters),
  );
}

export async function getRecentFeedback(
  filters: Filters,
  limit = 5,
): Promise<ApiEnvelope<FeedbackRow[]>> {
  return apiFetch<ApiEnvelope<FeedbackRow[]>>(
    buildApiUrl('/salescoach/api/overview/recent-feedback', filters, { limit }),
  );
}

export async function getSyncStatus(): Promise<ApiEnvelope<SyncStatus>> {
  return apiFetch<ApiEnvelope<SyncStatus>>(`/salescoach/api/sync-status`);
}

/**
 * `/salescoach/api/operations/time-saved-series` — bucketed Overview chart.
 * Router groups by compute, not tab, so this lives under `/operations/*`
 * even though the widget renders on the Overview page.
 */
export async function getTimeSavedSeries(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<TimeSavedSeries>> {
  return apiFetch<ApiEnvelope<TimeSavedSeries>>(
    buildApiUrl('/salescoach/api/operations/time-saved-series', filters, { granularity }),
  );
}

/**
 * `/salescoach/api/overview/time-saved-series/breakdown` — per-bucket drill-down
 * (Task 3 — v1.4 dashboard-ux-fix). Returns the per-user minutes-saved split
 * for a single clicked bucket so TimeSavedBreakdownModal can render a sorted
 * list with deep-links into `/user/:username`. The server narrows the window
 * to `[bucket, bucket + step(granularity))`, so `bucket` MUST be the ISO
 * timestamp the chart already rendered — not a rounded display string.
 */
export async function getTimeSavedBreakdown(
  filters: Filters,
  bucket: string,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<TimeSavedBreakdownResponse>> {
  return apiFetch<ApiEnvelope<TimeSavedBreakdownResponse>>(
    buildApiUrl('/salescoach/api/overview/time-saved-series/breakdown', filters, {
      bucket,
      granularity,
    }),
  );
}

/**
 * Per-trace details for a clicked Time-saved bucket — feeds the sortable
 * drill-down table. Same `[bucket, bucket + step(granularity))` window as
 * `getTimeSavedBreakdown` so the sum of the returned `timeSavedMinutes`
 * reconciles with the bar's height (minus any rows capped by LIMIT).
 */
export async function getTimeSavedBreakdownItems(
  filters: Filters,
  bucket: string,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<TimeSavedBreakdownItemsResponse>> {
  return apiFetch<ApiEnvelope<TimeSavedBreakdownItemsResponse>>(
    buildApiUrl('/salescoach/api/overview/time-saved-series/breakdown/items', filters, {
      bucket,
      granularity,
    }),
  );
}

/**
 * `/salescoach/api/traces/list` — shared chart drill-down (v1.4 UX fix).
 *
 * One wrapper backs every TraceDrillDownModal caller. Pass `extras` to narrow
 * to the clicked chart slice (rating, latencyBand, complexity, bucket). When
 * all extras are omitted, the endpoint lists every trace in the window — the
 * server still enforces the global FilterBar (role / cohort / users / day
 * filter / weekend exclusion), so totals reconcile with whatever chart the
 * modal opened from.
 */
export async function getTracesList(
  filters: Filters,
  extras: TraceListExtras = {},
): Promise<ApiEnvelope<TraceListResponse>> {
  // TraceListExtras keys all serialise as primitive scalars — `ApiUrlExtras`
  // accepts `string | number | boolean | undefined`, matching every field
  // below. Undefined entries are dropped by `buildApiUrl`.
  return apiFetch<ApiEnvelope<TraceListResponse>>(
    buildApiUrl('/salescoach/api/traces/list', filters, { ...extras }),
  );
}
